/**
 * @file picking-search.ts
 * @description Разбор ввода поля сборки и фильтр строк задания (phase19.23).
 *
 * Порт чистой логики мобильного приложения
 * (`apps/mobile/admin/lib/features/wms/state/picking_search.dart`) — правило
 * «что делает ввод» должно быть одинаковым на всех клиентах, иначе кладовщик
 * переучивается при переходе с ТСД на ноутбук.
 *
 * Логика без React и сети: фильтруем то, что уже на экране (работает без
 * запроса в каталог), а решение «отметить позицию или отфильтровать список»
 * принимается одной функцией и покрывается тестами.
 *
 * Разбор префиксов — общий роутер `parseScan` (`lib/scan-payload.ts`, зеркало
 * серверного `src/barcode/scan-payload.ts`, W17-3, phase19.22): `LOC:A-02-03`
 * — место, а не артикул, в фильтр не попадает; `SKU:`/`LOT:`/этикетка склада,
 * «Честный знак» и GTIN — код товара, он всегда уходит на сервер, даже если
 * набран руками (фильтровать список по `SKU:CHB…` бессмысленно).
 */

import type { PickingTaskItemDto } from "./api-picking";
import { isGoodsScan, parseScan } from "./scan-payload";

/** Что кладовщик ввёл или отсканировал — явные варианты вместо флагов. */
export type PickingInput =
  /** Пустой ввод: реагировать не на что. */
  | { kind: "empty" }
  /** Штрихкод ячейки: `LOC:a-02-03` → `A-02-03`. */
  | { kind: "bin"; binCode: string }
  /** Код товара с нашим префиксом, ЧЗ или GTIN — разбирает сервер. */
  | { kind: "goods"; code: string }
  /** Всё остальное: артикул, ШК товара или произвольный текст поиска. */
  | { kind: "code"; code: string };

/** Разбор ввода: ячейка / код / пусто. */
export function parsePickingInput(raw: string): PickingInput {
  const value = raw.trim();
  if (!value) return { kind: "empty" };
  const parsed = parseScan(value);
  if (parsed.kind === "bin") return { kind: "bin", binCode: parsed.address };
  // Битый код нашего префикса тоже уходит на сервер: он ответит понятной
  // ошибкой (`scan_code_invalid`), а не молчаливым пустым фильтром.
  if (isGoodsScan(parsed) || parsed.kind === "invalid") return { kind: "goods", code: value };
  return { kind: "code", code: value };
}

/**
 * Точное совпадение с артикулом строки задания (регистронезависимо).
 *
 * Нужно, чтобы отличить «кладовщик ввёл артикул позиции» (отмечаем) от
 * «кладовщик ищет строку» (фильтруем). Сверяем оба кода строки — так же, как
 * сервер в `PickingExecutionService.scan`.
 */
export function findItemByExactCode(
  items: PickingTaskItemDto[],
  code: string,
): PickingTaskItemDto | null {
  const needle = code.trim().toUpperCase();
  if (!needle) return null;
  return (
    items.find(
      (item) =>
        item.skuCode.toUpperCase() === needle ||
        item.sku.code.toUpperCase() === needle,
    ) ?? null
  );
}

/**
 * Фильтр уже загруженных строк задания по подстроке.
 *
 * Ищем по артикулу, названию товара, фасовке и коду ячейки —
 * регистронезависимо, как в мобильном.
 */
export function filterPickingItems(
  items: PickingTaskItemDto[],
  query: string,
): PickingTaskItemDto[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((item) => {
    const haystack = [
      item.skuCode,
      item.sku.code,
      item.sku.productName,
      item.sku.displayName,
      item.binCode,
    ];
    return haystack.some((part) => part?.toLowerCase().includes(needle));
  });
}

/** Что экран делает с завершённым вводом. */
export type PickingRouting =
  /** Пустая строка — не реагируем. */
  | "ignore"
  /** Штрихкод ячейки: подтверждение места тостом, фильтр не трогаем. */
  | "bin"
  /** Отметить позицию (скан или точный артикул строки). */
  | "markItem"
  /** Оставить как фильтр по строкам задания. */
  | "filter";

interface RouteInput {
  /** Введённое значение (Enter в поле). */
  value: string;
  /** true — ввод пришёл со сканера (см. `isScannerPace`). */
  fromScanner: boolean;
  items: PickingTaskItemDto[];
  /** Задание живое и своё: отмечать позиции можно. */
  taskActive: boolean;
}

/**
 * Маршрутизация ввода поля сборки — одно правило на экран и на тесты.
 *
 * Со сканера код всегда уходит на сервер (он же разбирает коды и отвечает
 * понятной ошибкой); руками отмечает позицию только точный артикул строки,
 * остальное — поисковый запрос. Закрытое задание отмечать нечем: поле работает
 * как фильтр.
 */
export function routePickingInput({
  value,
  fromScanner,
  items,
  taskActive,
}: RouteInput): PickingRouting {
  const parsed = parsePickingInput(value);
  if (parsed.kind === "empty") return "ignore";
  if (parsed.kind === "bin") return "bin";
  if (!taskActive) return "filter";
  if (fromScanner || parsed.kind === "goods") return "markItem";
  return findItemByExactCode(items, parsed.code) ? "markItem" : "filter";
}

/**
 * Минимум символов, по которым имеет смысл судить о скорости ввода.
 * Короткие «12» + Enter руками и со сканера неразличимы — считаем ручными.
 */
const SCAN_MIN_CHARS = 3;

/** Средний интервал между символами, выше которого это уже человек, мс. */
const SCAN_MAX_AVG_MS = 45;

/**
 * Пришёл ли ввод со сканера — по темпу набора.
 *
 * В вебе USB-сканер работает как клавиатура (`feedback_warehouse_ui_mobile_first`),
 * отдельного источника, как камера в мобильном, у поля нет. Различаем по
 * скорости: сканер «печатает» пачку за миллисекунды, быстрый человек — от
 * ~70 мс на символ. Вставка из буфера (`elapsedMs` ≈ 0) тоже считается сканом:
 * пользователь вставил готовый код, а не поисковую фразу.
 */
export function isScannerPace(charCount: number, elapsedMs: number): boolean {
  if (charCount < SCAN_MIN_CHARS) return false;
  if (elapsedMs < 0) return false;
  return elapsedMs <= charCount * SCAN_MAX_AVG_MS;
}
